import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "styles.module.css";
import Filter from "./Filter";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addMonths, addWeeks } from "date-fns";
import OverLayLoading from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { GridCellProps } from "@progress/kendo-react-grid";
import { PageTypePath } from "utils/pages";
import { ICON_NAMES } from "utils/constant";
import { PurchaseOverview as PurchaseOverviewItem } from "interface/sales";

import {
	BuyerOverviewGridColumnItem,
	purchaseOverviewDetailGridItems,
} from "./utils/GridColumns";
import {
	getPurchaseOverviewApi,
	getPurchaseOverviewLinesApi,
	PurchaseOverviewFilter,
} from "api/sales";
import CustomDetailedPage from "ui/grid/DetailPage";
import { useUserStore } from "store";
import { UserRole } from "interface/user";
import PurchaseOverviewMobileList from "./PurchaseOverviewMobileList";
import "./style.css";

const pageSize = 60;

const PurchaseOverview = () => {
	const { t } = useTranslation();
	const { roles } = useUserStore();

	const overlayRef = useRef<HTMLDivElement>(null);

	const gridRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<PurchaseOverviewItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	var fromDate = addMonths(new Date(), -1);

	if (roles.includes(UserRole.Admin)) {
		fromDate = addWeeks(new Date(), -1);
	}

	const [filterOptions, setFilterOptions] = useState<PurchaseOverviewFilter>({
		take: pageSize,
		skip: 0,
		fromDate: fromDate,
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = useCallback(
		(skip: number, take: number, resetData = false) => {
			if (mobileRequestInProgress.current || mobileDataFinished.current) {
				return;
			}

			mobileRequestInProgress.current = true;
			setMobileLoading(true);

			getPurchaseOverviewApi({
				...filterOptions,
				skip,
				take,
			})
				.then((response) => {
					const incoming = (response?.data || []) as PurchaseOverviewItem[];

					if (incoming.length === 0) {
						mobileDataFinished.current = true;
						if (resetData) {
							setMobileData([]);
						}
						return;
					}

					mobileNextSkip.current = skip + take;

					if (resetData) {
						setMobileData(incoming);
					} else {
						setMobileData((prev) => [...prev, ...incoming]);
					}

					const loadedCount = skip + incoming.length;
					if (loadedCount >= (response?.totalCount || 0)) {
						mobileDataFinished.current = true;
					}
				})
				.finally(() => {
					mobileRequestInProgress.current = false;
					setMobileLoading(false);
				});
		},
		[filterOptions],
	);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const openEdit = (prop: GridCellProps) => {
		// navigate(`/`);
		// window.open(
		// 	`/${PageTypePath.LandingNoteForm}/${prop.dataItem.noteNumber}/0`
		// );
	};

	const handleLinesPdfDown = (props: GridCellProps, id: string) => {

		if (props.dataItem.source === 2) {
			localStorage.setItem("noteNumber", props.dataItem.number);
			localStorage.setItem("pdfType", "SalesNote");
		}
		else if (props.dataItem.source === 3 || props.dataItem.source === 5) {
			localStorage.setItem("noteNumber", props.dataItem.number);
			localStorage.setItem("pdfType", "SalesConfirmation");
		}
		else if (props.dataItem.source === 4) {
			localStorage.setItem("noteNumber", props.dataItem.number);
			localStorage.setItem("pdfType", "Contract");
		}

		window.open(`/${PageTypePath.Pdf}/${props.dataItem.reportGuid}`, "_blank");
		return;
	};

	return (
		<div className={`${styles.PageContainer} purchase-overview-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("DocumentOverview")}
				</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<PurchaseOverviewMobileList
						items={mobileData}
						loading={mobileLoading}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="salesId"
						gridColumns={BuyerOverviewGridColumnItem()}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						lastColWidth="130px"
						detailPage={(prop) => (
							<CustomDetailedPage
								grids={purchaseOverviewDetailGridItems}
								dataApi={() =>
									getPurchaseOverviewLinesApi({
										salesId: prop.dataItem.salesId,
										source: prop.dataItem.source,
									})
								}
								lastColBtns={[
									{
										notEqualToValue: 1,
										conditionField: "source",
										iconName: ICON_NAMES.Download,
										onclick: (props) =>
											handleLinesPdfDown(props, prop.dataItem.salesId),
									},
								]}
							/>
						)}
						lastColBtns={[
							{
								iconName: ICON_NAMES.Edit,
								conditionField: "source",
								showConditionValue: 0,
								onclick: openEdit,
							},
						]}
						exportGridAsExcelRef={excelRef}
						apiMethod={getPurchaseOverviewApi}
					/>
				)}
			</div>
		</div>
	);
};

export default PurchaseOverview;
